import { getIntl } from '../../../../../../utils/intl';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { Action } from '../../../../../v2BaseComponents/ActionButtonLayout/types';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import useMedicalProfileFiles from '../../../../../../hooks/api/useMedicalProfileFiles';
import { useEffect, useState } from 'react';
import { Input } from '../../../../../v2BaseComponents/Inputs';

const intl = getIntl('EditFolderButton', {
    promptHeader: 'Edit Folder',
    folderName: 'Folder Name',
    save: 'Save',
});

export type UseEditFolderActionArgs = {
    folderId: string;
    medicalProfileId: string;
    onSetFolderId: (folderId: string) => void;
};

export type UseEditFolderActionReturnValue = {
    action: Action | null;
    Prompt: React.ReactNode;
};

export const useEditFolderAction = (args: UseEditFolderActionArgs): UseEditFolderActionReturnValue => {
    const { folderMetadata } = useMedicalProfileFiles({
        medicalProfileId: args.medicalProfileId,
        folderId: args.folderId,
    });
    const [folderName, setFolderName] = useState('');
    const currentFolderName =
        folderMetadata?.targetFolder.isRoot === false ? folderMetadata?.targetFolder.folder.folderName : '';
    useEffect(() => {
        setFolderName(currentFolderName || '');
    }, [currentFolderName]);
    const { isUpdatingFolder, onUpdateFolder } = useMedicalProfileFiles({
        medicalProfileId: args.medicalProfileId,
        folderId: args.folderId,
    });
    const promptButton = usePromptButton({
        promptTitle: intl('promptHeader'),
        renderPromptContents: (onClose) =>
            folderMetadata?.targetFolder.isRoot === false ? (
                <ContentStack>
                    <Input
                        title={intl('folderName')}
                        value={folderName}
                        onChange={(e) => {
                            setFolderName(e.nativeEvent.text);
                        }}
                        testID="folder-name-input"
                    />
                    <PromptActions
                        actions={[
                            {
                                title: intl('save'),
                                isDisabled: isUpdatingFolder || !folderName,
                                onPress: () => {
                                    onUpdateFolder({ folderId: args.folderId, folderName });
                                    onClose();
                                },
                                testID: 'edit-folder-confirm-button',
                                icon: 'CheckCircle',
                            },
                        ]}
                    />
                </ContentStack>
            ) : null,
    });

    if (!folderMetadata || folderMetadata.targetFolder.isRoot === true) return { action: null, Prompt: null };

    return {
        action: {
            title: intl('promptHeader'),
            icon: 'Edit2',
            onPress: promptButton.onPress,
            show: true,
            testID: 'edit-folder-button',
        },
        Prompt: promptButton.Prompt,
    };
};
